import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  InputAdornment,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  ConfirmationNumber as SerialIcon
} from '@mui/icons-material';
import {
  getProducts,
  getOfficeIssues,
  createOfficeIssue,
  getProductSerials
} from '../../services/api';

const money = (v) =>
  Number(v || 0).toLocaleString('en-LK', { style: 'currency', currency: 'LKR' });

/** Popup to pick exactly `quantity` available serials */
const SerialPickerDialog = ({ open, product, quantity, initialSelected = [], onClose, onConfirm }) => {
  const [serials, setSerials] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open || !product) return;
    setSearch('');
    setError('');
    setSelected(initialSelected.map((s) => ({ serial_number: s })));
    const load = async () => {
      try {
        setLoading(true);
        const res = await getProductSerials(product.id, { status: 'available' });
        setSerials(res.data || []);
      } catch (err) {
        setError(err.message || 'Failed to load serials');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [open, product, quantity]);

  const filtered = serials.filter((s) => {
    if (!search.trim()) return true;
    return String(s.serial_number || '').toLowerCase().includes(search.toLowerCase());
  });

  const isChecked = (serial) =>
    selected.some((s) => s.serial_number === serial.serial_number);

  const toggle = (serial) => {
    setError('');
    setSelected((prev) => {
      const exists = prev.some((s) => s.serial_number === serial.serial_number);
      if (exists) return prev.filter((s) => s.serial_number !== serial.serial_number);
      if (prev.length >= quantity) {
        setError(`Select exactly ${quantity} serial number(s)`);
        return prev;
      }
      return [...prev, serial];
    });
  };

  const handleOk = () => {
    if (selected.length !== quantity) {
      setError(`Please select exactly ${quantity} serial number(s). Selected: ${selected.length}`);
      return;
    }
    onConfirm(selected.map((s) => s.serial_number));
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <SerialIcon color="primary" />
          Select serials — {product?.name}
        </Box>
        <Typography variant="body2" color="text.secondary">
          Select exactly {quantity} serial number(s) ({selected.length} selected / {serials.length} available)
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>{error}</Alert>}
        <TextField
          fullWidth
          size="small"
          placeholder="Search serial number..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon /></InputAdornment>
            )
          }}
        />
        {loading ? (
          <Box display="flex" justifyContent="center" py={3}><CircularProgress /></Box>
        ) : (
          <List dense sx={{ maxHeight: 360, overflow: 'auto' }}>
            {filtered.length === 0 && (
              <ListItem><ListItemText primary="No available serials found" /></ListItem>
            )}
            {filtered.map((serial) => (
              <ListItem key={serial.id || serial.serial_number} disablePadding>
                <ListItemButton onClick={() => toggle(serial)} dense>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Checkbox edge="start" checked={isChecked(serial)} tabIndex={-1} disableRipple />
                  </ListItemIcon>
                  <ListItemText
                    primary={serial.serial_number}
                    secondary={serial.current_location || serial.batch_number || null}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleOk}
          disabled={selected.length !== quantity || loading}
        >
          Use {selected.length}/{quantity} serials
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const OfficeIssue = () => {
  const [issues, setIssues] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [historySearch, setHistorySearch] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewIssue, setViewIssue] = useState(null);
  const [productSearch, setProductSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [purpose, setPurpose] = useState('');
  const [notes, setNotes] = useState('');
  const [selected, setSelected] = useState([]); // { product, quantity, serial_numbers }

  const [serialPicker, setSerialPicker] = useState({
    open: false,
    product: null,
    quantity: 1,
    productId: null
  });

  const loadIssues = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await getOfficeIssues(historySearch ? { search: historySearch } : {});
      setIssues(res.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load office issues');
    } finally {
      setLoading(false);
    }
  }, [historySearch]);

  const loadProducts = useCallback(async (search = '') => {
    try {
      const params = { limit: 500, active: 'true' };
      if (search) params.search = search;
      const res = await getProducts(params);
      setProducts(res.data || []);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    loadIssues();
  }, [loadIssues]);

  useEffect(() => {
    const t = setTimeout(() => loadProducts(productSearch.trim()), productSearch ? 300 : 0);
    return () => clearTimeout(t);
  }, [productSearch, loadProducts]);

  const categories = Array.from(
    new Set(products.map((p) => p.category).filter(Boolean))
  ).sort();

  const filteredProducts = products.filter((p) => {
    if (categoryFilter !== 'all' && p.category !== categoryFilter) return false;
    const q = productSearch.toLowerCase().trim();
    if (!q) return true;
    const serialMatch = Array.isArray(p.serials) && p.serials.some((s) =>
      String(s.serial_number || '').toLowerCase().includes(q)
    );
    return (
      String(p.name || '').toLowerCase().includes(q) ||
      String(p.product_code || '').toLowerCase().includes(q) ||
      String(p.barcode || '').toLowerCase().includes(q) ||
      String(p.category || '').toLowerCase().includes(q) ||
      serialMatch
    );
  });

  const isSelected = (productId) => selected.some((s) => s.product.id === productId);

  const needsSerials = (product) =>
    Boolean(product?.track_serials) ||
    (Array.isArray(product?.serials) && product.serials.some((s) => s.status === 'available'));

  const toggleSelect = (product) => {
    if (isSelected(product.id)) {
      setSelected((prev) => prev.filter((s) => s.product.id !== product.id));
      return;
    }
    const stock = Math.max(0, parseInt(product.good_quantity, 10) || parseInt(product.quantity, 10) || 0);
    if (stock < 1) {
      setError(`${product.name} has no stock`);
      return;
    }
    setSelected((prev) => [
      ...prev,
      { product, quantity: 1, serial_numbers: '' }
    ]);
  };

  const updateSelected = (productId, field, value) => {
    setSelected((prev) =>
      prev.map((s) => {
        if (s.product.id !== productId) return s;
        const next = { ...s, [field]: value };
        // Clear serials if qty changes so user re-picks
        if (field === 'quantity') next.serial_numbers = '';
        return next;
      })
    );
  };

  const removeSelected = (productId) => {
    setSelected((prev) => prev.filter((s) => s.product.id !== productId));
  };

  const openSerialPicker = (row) => {
    const qty = parseInt(row.quantity, 10) || 1;
    setSerialPicker({
      open: true,
      product: row.product,
      quantity: qty,
      productId: row.product.id
    });
  };

  const handleSerialConfirm = (serialList) => {
    const productId = serialPicker.productId;
    setSelected((prev) =>
      prev.map((s) =>
        s.product.id === productId
          ? { ...s, serial_numbers: serialList.join(', ') }
          : s
      )
    );
  };

  const openCreate = () => {
    setPurpose('');
    setNotes('');
    setSelected([]);
    setProductSearch('');
    setCategoryFilter('all');
    setError('');
    setDialogOpen(true);
    loadProducts('');
  };

  const handleIssue = async () => {
    if (!purpose.trim()) {
      setError('Please enter the purpose');
      return;
    }
    if (selected.length === 0) {
      setError('Select at least one product');
      return;
    }
    for (const row of selected) {
      const qty = parseInt(row.quantity, 10) || 0;
      if (qty < 1) {
        setError(`Invalid quantity for ${row.product.name}`);
        return;
      }
      if (needsSerials(row.product)) {
        const serials = String(row.serial_numbers || '')
          .split(/[,;\n]+/)
          .map((s) => s.trim())
          .filter(Boolean);
        if (serials.length !== qty) {
          setError(`${row.product.name}: click Serial numbers box and select ${qty} serial(s)`);
          return;
        }
      }
    }

    try {
      setSaving(true);
      setError('');
      const payload = {
        purpose: purpose.trim(),
        notes: notes.trim() || null,
        items: selected.map((row) => ({
          product_id: row.product.id,
          quantity: parseInt(row.quantity, 10) || 1,
          serial_numbers: row.serial_numbers || null
        }))
      };
      const res = await createOfficeIssue(payload);
      if (res.success) {
        setSuccess(res.message || 'Issued successfully');
        setDialogOpen(false);
        loadIssues();
        setTimeout(() => setSuccess(''), 4000);
      } else {
        setError(res.message || 'Failed to issue');
      }
    } catch (err) {
      setError(err.message || 'Failed to issue products');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={2}>
        <Box>
          <Typography variant="h4" fontWeight={600}>Issue Sun System Office</Typography>
          <Typography color="text.secondary">
            Issue stock to Sun Systems office — reduces product quantity and keeps a full history
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button startIcon={<RefreshIcon />} onClick={loadIssues}>Refresh</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Issue Products
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search history by issue #, purpose, issued by..."
          value={historySearch}
          onChange={(e) => setHistorySearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && loadIssues()}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon /></InputAdornment>
            )
          }}
        />
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Issue #</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Purpose</TableCell>
                <TableCell>Items</TableCell>
                <TableCell>Qty</TableCell>
                <TableCell>Issued By</TableCell>
                <TableCell>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {issues.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">No office issues yet</TableCell>
                </TableRow>
              )}
              {issues.map((issue) => (
                <TableRow key={issue.id} hover>
                  <TableCell><strong>{issue.issue_number}</strong></TableCell>
                  <TableCell>
                    {issue.created_at
                      ? new Date(issue.created_at).toLocaleString('en-GB')
                      : '—'}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>{issue.purpose}</TableCell>
                  <TableCell>{issue.total_items}</TableCell>
                  <TableCell>{issue.total_quantity}</TableCell>
                  <TableCell>{issue.issued_by_name || '—'}</TableCell>
                  <TableCell>
                    <Chip size="small" color="secondary" label={issue.status || 'issued'} />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => setViewIssue(issue)}>
                      <ViewIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Issue to Sun System Office</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Purpose *"
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                placeholder="e.g. Office use, demo, internal transfer"
                multiline
                minRows={2}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Notes (optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                multiline
                minRows={2}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom>Select Products</Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by name, barcode, serial number, category..."
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"><SearchIcon /></InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                size="small"
                label="Category"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <MenuItem value="all">All categories</MenuItem>
                {categories.map((c) => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 280, mb: 2 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Product</TableCell>
                  <TableCell>Code / Barcode</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell align="right">Stock</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredProducts.slice(0, 100).map((p) => {
                  const stock = Math.max(0, parseInt(p.good_quantity, 10) || parseInt(p.quantity, 10) || 0);
                  const checked = isSelected(p.id);
                  return (
                    <TableRow
                      key={p.id}
                      hover
                      selected={checked}
                      onClick={() => stock > 0 && toggleSelect(p)}
                      sx={{ cursor: stock > 0 ? 'pointer' : 'not-allowed', opacity: stock > 0 ? 1 : 0.5 }}
                    >
                      <TableCell padding="checkbox">
                        <Chip size="small" label={checked ? 'Selected' : 'Select'} color={checked ? 'primary' : 'default'} />
                      </TableCell>
                      <TableCell>{p.name}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{p.product_code}</Typography>
                        <Typography variant="caption" color="text.secondary">{p.barcode || '—'}</Typography>
                      </TableCell>
                      <TableCell>{p.category || '—'}</TableCell>
                      <TableCell align="right">{stock}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {selected.length > 0 && (
            <>
              <Typography variant="subtitle1" gutterBottom>Selected for issue ({selected.length})</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell width={100}>Qty</TableCell>
                      <TableCell>Serial numbers</TableCell>
                      <TableCell width={60} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selected.map((row) => {
                      const serialRequired = needsSerials(row.product);
                      const serialCount = String(row.serial_numbers || '')
                        .split(/[,;\n]+/)
                        .map((s) => s.trim())
                        .filter(Boolean).length;
                      return (
                        <TableRow key={row.product.id}>
                          <TableCell>
                            {row.product.name}
                            <Typography variant="caption" display="block" color="text.secondary">
                              Stock: {row.product.good_quantity ?? row.product.quantity}
                              {serialRequired ? ' · Serial tracked' : ''}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              type="number"
                              value={row.quantity}
                              onChange={(e) => updateSelected(row.product.id, 'quantity', e.target.value)}
                              inputProps={{ min: 1 }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              fullWidth
                              value={row.serial_numbers}
                              placeholder={serialRequired ? 'Click to select serials' : 'Optional — click to select'}
                              onClick={() => openSerialPicker(row)}
                              onFocus={(e) => {
                                e.target.blur();
                                openSerialPicker(row);
                              }}
                              InputProps={{
                                readOnly: true,
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <SerialIcon fontSize="small" color="primary" />
                                  </InputAdornment>
                                )
                              }}
                              helperText={
                                serialRequired
                                  ? `${serialCount}/${parseInt(row.quantity, 10) || 1} selected — must match qty`
                                  : serialCount
                                    ? `${serialCount} selected`
                                    : 'Click to pick serials (optional)'
                              }
                              error={serialRequired && serialCount !== (parseInt(row.quantity, 10) || 1)}
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton size="small" color="error" onClick={() => removeSelected(row.product.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" color="secondary" onClick={handleIssue} disabled={saving}>
            {saving ? <CircularProgress size={22} /> : 'Issue'}
          </Button>
        </DialogActions>
      </Dialog>

      <SerialPickerDialog
        open={serialPicker.open}
        product={serialPicker.product}
        quantity={serialPicker.quantity}
        initialSelected={String(
          selected.find((s) => s.product.id === serialPicker.productId)?.serial_numbers || ''
        ).split(/[,;\n]+/).map((s) => s.trim()).filter(Boolean)}
        onClose={() => setSerialPicker((p) => ({ ...p, open: false }))}
        onConfirm={handleSerialConfirm}
      />

      <Dialog open={Boolean(viewIssue)} onClose={() => setViewIssue(null)} maxWidth="md" fullWidth>
        <DialogTitle>Issue {viewIssue?.issue_number}</DialogTitle>
        <DialogContent dividers>
          {viewIssue && (
            <>
              <Typography><strong>Purpose:</strong> {viewIssue.purpose}</Typography>
              <Typography><strong>Issued by:</strong> {viewIssue.issued_by_name || '—'}</Typography>
              <Typography><strong>Date:</strong> {viewIssue.created_at ? new Date(viewIssue.created_at).toLocaleString('en-GB') : '—'}</Typography>
              {viewIssue.notes && <Typography><strong>Notes:</strong> {viewIssue.notes}</Typography>}
              <TableContainer sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell>Code</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell>Serials</TableCell>
                      <TableCell align="right">Unit Cost</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(viewIssue.items || []).map((it) => (
                      <TableRow key={it.id}>
                        <TableCell>{it.product_name}</TableCell>
                        <TableCell>{it.product_code || it.barcode || '—'}</TableCell>
                        <TableCell>{it.category || '—'}</TableCell>
                        <TableCell align="right">{it.quantity}</TableCell>
                        <TableCell>
                          {Array.isArray(it.serial_numbers)
                            ? it.serial_numbers.join(', ')
                            : (it.serial_numbers || '—')}
                        </TableCell>
                        <TableCell align="right">{money(it.unit_cost)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewIssue(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default OfficeIssue;
